import { useReactiveVar } from '@apollo/client';
import { useApolloClient } from '@apollo/react-hooks';
import { CALENDAR_EVENTS_SUBSCRIPTION } from 'api/graphql/v2/subscriptions/Events';
import { getView } from 'helpers/calendarHelper';
import { isPlannerRoute } from 'helpers/routeHelper';
import { handleSubscriptionDataUpdate } from 'helpers/subscriptionsHelper';
import { ICalendarLoading } from 'model/calendar';
import { EventType } from 'model/calendar/events';
import { calendarGroup } from 'model/calendar/groups';
import { IEvent } from 'model/v2';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import type { Observable } from 'zen-observable-ts';
import { customizeRange } from 'utils/builders/calendar';
import {
  CalendarView,
  mainCalendarPrefDayVar,
  plannerCalendarPrefViewVar
} from 'utils/cache/calendar';
import {
  calendarEventsPrefVar,
  calendarFiltersPrefVar,
  clientsResourcesPrevVar,
  providerResourcesPrevVar
} from 'utils/cache/filters';
import { EVENT_TYPES } from 'utils/constants/appointmentsTypes';
import { internalFilters } from 'utils/constants/calendarFilters';
import { FEATURES, getFeatureAvailability } from 'utils/featureToggle';
import { mapToCalendarEventsV2 } from 'utils/mappers/response/eventsV2';

// Minimal structural type — we only need unsubscribe().
type ZenSubscription = { unsubscribe: () => void };

type ApolloLike = {
  subscribe: (options: { query: any; variables?: any }) => Observable<any>;
};

const CALENDAR_ID = 'calendarID';

let refCount = 0;
let activeSub: ZenSubscription | null = null;
let activeKey: string | null = null;
let teardownTimer: ReturnType<typeof setTimeout> | null = null;

const currentHandlerBox: {
  current: ((events: IEvent[]) => void) | null;
} = { current: null };

// Current isCalendarLoading value, mirrored from the hook prop.
const loadingBox: { current: ICalendarLoading | null } = { current: null };

const serializeKey = (startDate: Date, endDate: Date) =>
  `${startDate instanceof Date ? startDate.toISOString() : String(startDate)}|${
    endDate instanceof Date ? endDate.toISOString() : String(endDate)
  }`;

const startSubscription = (
  client: ApolloLike,
  startDate: Date,
  endDate: Date
) => {
  const key = serializeKey(startDate, endDate);
  if (teardownTimer !== null) {
    clearTimeout(teardownTimer);
    teardownTimer = null;
  }
  if (activeSub && activeKey === key) {
    return;
  }
  if (activeSub) {
    console.log(
      '[useEventSubscriptionHook] date range changed, restarting subscription'
    );
    activeSub.unsubscribe();
    activeSub = null;
  }

  console.log(
    '[useEventSubscriptionHook] starting singleton subscription for range:',
    key
  );
  activeKey = key;
  activeSub = client
    .subscribe({
      query: CALENDAR_EVENTS_SUBSCRIPTION,
      variables: { startDate, endDate }
    })
    .subscribe({
      next: ({ data }: { data?: { calendarEvents?: IEvent[] } }) => {
        const calendarEvents = data?.calendarEvents;
        if (!calendarEvents || calendarEvents.length === 0) return;
        if (!isPlannerRoute(window.location.pathname)) {
          return;
        }
        const handler = currentHandlerBox.current;
        if (handler) {
          handler(calendarEvents);
        }
      },
      error: (err: any) => {
        console.error(
          '[useEventSubscriptionHook] subscription error:',
          err
        );
      }
    });
};

const scheduleTeardown = () => {
  if (teardownTimer !== null) return;
  teardownTimer = setTimeout(() => {
    teardownTimer = null;
    if (refCount > 0) return;
    console.log(
      '[useEventSubscriptionHook] tearing down singleton subscription'
    );
    activeSub?.unsubscribe();
    activeSub = null;
    activeKey = null;
  }, 0);
};

export const useEventSubscriptionHook = (
  isCalendarLoading: ICalendarLoading
) => {
  const location = useLocation();
  const onPlannerPage = isPlannerRoute(location?.pathname);

  const filters = useReactiveVar(calendarFiltersPrefVar);
  const calendarView = useReactiveVar(plannerCalendarPrefViewVar);
  const calendarDate = useReactiveVar(mainCalendarPrefDayVar);
  const providers = useReactiveVar(providerResourcesPrevVar);
  const clients = useReactiveVar(clientsResourcesPrevVar);

  const client = (useApolloClient() as unknown) as ApolloLike;

  // Cleanup any outstanding retry timers on unmount.
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  // Keep loading state mirrored so the module-level handler can check it.
  useEffect(() => {
    loadingBox.current = isCalendarLoading;
  }, [isCalendarLoading]);

  // Compute the current date range once per render so the two places that
  // need it (the subscription key and potential logs) agree.
  const range = customizeRange(calendarDate, getView(calendarView));
  const startDate = range.start;
  const endDate = range.end;

  useEffect(() => {
    currentHandlerBox.current = (calendarEvents: IEvent[]) => {
      console.log('subscription received subscription data:', calendarEvents);

      const loading = loadingBox.current;
      if (
        !calendarEvents ||
        (loading &&
          (loading.isLoadingClientsEvents || loading.isLoadingStaffEvents))
      ) {
        return;
      }

      const isPendingFilter = (filters.internals as internalFilters[])?.includes(
        internalFilters.pending
      );
      const isCanceledFilter = (filters.internals as internalFilters[])?.includes(
        internalFilters.canceled
      );
      const isDeletedFilter = (filters.internals as internalFilters[])?.includes(
        internalFilters.deleted
      );

      const { clientEvents, providerEvents } = formatEventResult(
        calendarEvents,
        calendarView,
        calendarDate,
        isPendingFilter
      );

      if (providerEvents.length === 0 && clientEvents.length === 0) return;

      const shouldShowCanceled =
        !providerEvents[0]?.isOriginal && isCanceledFilter;
      const shouldShowDelete =
        !providerEvents[0]?.isOriginal && isDeletedFilter;

      const providerIds = providers.map(provider => provider.id);
      const clientIds = clients.map(client => client.id);

      const finalClientEvents = clientEvents.filter(event => {
        const clientId = event.client?.id;
        return clientId && clientIds.includes(clientId);
      });
      const finalProviderEvents = providerEvents.filter(event => {
        const providerId = event.provider?.id;
        return providerId && providerIds.includes(providerId);
      });

      const events = calendarEventsPrefVar();
      let updatedEvents = handleSubscriptionDataUpdate(
        finalProviderEvents,
        events,
        CALENDAR_ID,
        shouldShowCanceled,
        shouldShowDelete
      );
      updatedEvents = handleSubscriptionDataUpdate(
        finalClientEvents,
        updatedEvents,
        CALENDAR_ID,
        shouldShowCanceled,
        shouldShowDelete
      );
      calendarEventsPrefVar(updatedEvents);
    };
  }, [filters.internals, calendarView, calendarDate, providers, clients]);
  const rangeKey = serializeKey(startDate, endDate);
  useEffect(() => {
    if (!onPlannerPage) return;
    if (!getFeatureAvailability(FEATURES.CALENDARS_SUBSCRIPTIONS)) return;

    refCount += 1;
    startSubscription(client, startDate, endDate);
    return () => {
      refCount = Math.max(0, refCount - 1);
      if (refCount === 0) {
        scheduleTeardown();
      }
    };
  }, [client, onPlannerPage, rangeKey]);
};

const formatEventResult = (
  calendarEvents: IEvent[],
  calendarView: CalendarView,
  calendarDate: Date,
  isPendingFilter: boolean
) => {
  // For canceled/deleted events, use originalEventId as the id
  // so that the calendarID will match the existing event in the calendar
  const processedEvents = calendarEvents.map(event => {
    const isCanceledOrDeletedEvent =
      (event as any).__typename === 'CanceledEvent' ||
      event.status === 'canceled' ||
      event.status === 'deleted';
    const originalEventId = event.originalEventId;

    // If this is a canceled/deleted event with originalEventId, use that as
    // the id so calendarID (staff:${id} or client:${id}) matches the original.
    const effectiveId =
      isCanceledOrDeletedEvent && originalEventId ? originalEventId : event.id;

    return {
      ...event,
      id: effectiveId
    };
  });

  processedEvents.forEach(event => {
    event.type =
      event.appointmentType?.eventType?.name === EVENT_TYPES.Admin
        ? EventType.adminEvent
        : EventType.clientEvent;
  });

  const { finalEvents: clientEvents } = mapToCalendarEventsV2(
    processedEvents,
    calendarGroup.client,
    calendarView,
    calendarDate
  );

  const { finalEvents: providerEvents } = mapToCalendarEventsV2(
    processedEvents,
    calendarGroup.provider,
    calendarView,
    calendarDate,
    [],
    isPendingFilter
  );

  return { clientEvents, providerEvents };
};
